import type { DashboardBlockType } from "@/components/page/DashboardBlocks";
import { localStorageManager } from "@/services/localStorage";
import { useState } from "react";

export interface DashboardLayout {
  layout: DashboardBlockType[];
}

const getTenantMap = (key: string): Record<string, DashboardLayout> => {
  try {
    return JSON.parse(localStorageManager.getItem(key) ?? "") as Record<
      string,
      DashboardLayout
    >;
  } catch (err) {
    return {};
  }
};

export const useDashboardLayout = (
  key: string,
  tenantId: string,
  defaultLayout: DashboardLayout
) => {
  const [, refresh] = useState({});

  let layout: DashboardLayout = defaultLayout;

  const tenantMap = getTenantMap(key);
  if (tenantMap[tenantId]) {
    const savedLayout = tenantMap[tenantId];

    // get valid blocks
    const valid = new Set(defaultLayout.layout.map((block) => block.id));

    // get used blocks
    const used = new Set(savedLayout.layout.map((block) => block.id));

    layout = { layout: [] };

    // push valid saved blocks to layout
    for (const block of savedLayout.layout) {
      if (valid.has(block.id)) layout.layout.push(block);
    }
    // push unsaved, new blocks to layout
    for (const block of defaultLayout.layout) {
      if (!used.has(block.id)) layout.layout.push(block);
    }
  }

  const saveDashboardLayout = (layout: DashboardLayout) => {
    const tenantMap = getTenantMap(key); // get fresh copy in case other tabs update it
    tenantMap[tenantId] = layout;
    localStorageManager.setItem(key, JSON.stringify(tenantMap));
  };
  const resetDashboardLayout = () => {
    const tenantMap = getTenantMap(key); // get fresh copy in case other tabs update it
    delete tenantMap[tenantId];
    localStorageManager.setItem(key, JSON.stringify(tenantMap));
    refresh({});
  };

  return {
    dashboardLayout: layout,
    saveDashboardLayout,
    resetDashboardLayout,
  };
};
